import { useRouter } from 'next/router'
import React from 'react'

import { useQueryGetBookingById } from '@flecto/core/saas'
import { useTranslation } from '@flecto/translations'
import { EInsuranceStatus } from '@flecto/types/src/enums/EInsuranceStatus'
import { Badge } from '@flecto/ui'
import { type ButtonVariant } from '@flecto/ui/lib/interfaces/Button'
import { type DesignSystemColors, colors } from '@flecto/ui/lib/theme'

import { getFormattedDate } from '../../../utils/getFormattedStatusDate'

/**
 * @function useStatus
 * @description get Booking Insurance Card Description and Icon
 */
export default function useStatus() {
  const {
    t,
    i18n: { language },
  } = useTranslation('saas', { keyPrefix: 'insurances' })
  const router = useRouter()
  const id = router.query.id as string
  const { data } = useQueryGetBookingById({ bookingId: id })

  const getIssueStatusBadge = React.useCallback(
    (status: EInsuranceStatus, until?: string) => {
      switch (status) {
        case EInsuranceStatus.ACTIVE:
          return (
            <Badge variant='primary'>
              {t('status.insurance_active', {
                replace: { date: getFormattedDate(until) },
              })}
            </Badge>
          )
        case EInsuranceStatus.FINISHED:
          return (
            <Badge variant='neutral'>{t('status.insurance_expired')}</Badge>
          )
        case EInsuranceStatus.WITHIN_CLAIM_PERIOD:
          return (
            <Badge variant='info'>
              {t('status.insurance_within_claim', {
                replace: { date: getFormattedDate(until) },
              })}
            </Badge>
          )
        default:
          return null
      }
    },
    [data, language]
  )

  const insuranceButtonColor: DesignSystemColors = React.useMemo(() => {
    return colors.neutral600 as DesignSystemColors
  }, [data, language])

  const insuranceButtonVariant = React.useMemo(() => {
    return 'secondary-neutral' as ButtonVariant
  }, [data, language])

  const insuranceButtonTextKey = React.useMemo(() => {
    return 'viewButton'
  }, [data, language])

  return {
    getIssueStatusBadge,
    insuranceButton: {
      color: insuranceButtonColor,
      variant: insuranceButtonVariant,
      textKey: insuranceButtonTextKey,
    },
  }
}
